import { useCallback, useEffect, useState } from "react";
import { Copy, History, Loader2, Play, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// AI Team — clinic-tuned specialist agents (lead finding, sales follow-ups,
// social packs, ad copy, cold email sequences, growth plans). Free cascade.
export const AgencyTab = () => {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async (agentId) => {
    try {
      const { data } = await api.get("/admin/agency/runs", { params: agentId ? { agent_id: agentId } : {} });
      setHistory(data.runs || []);
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    api.get("/admin/agency/agents").then((r) => {
      setAgents(r.data.agents || []);
      if (r.data.agents?.length) setSelected(r.data.agents[0].id);
    }).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const run = async () => {
    if (!input.trim()) {
      toast.error("Describe the task first");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/agency/run", { agent_id: selected, input: input.trim() }, { timeout: 240000 });
      setResult(data);
      toast.success(data.kind === "leads" || data.leads ? `${data.leads?.length || 0} prospects found` : `${data.agent_name} delivered`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Agent run failed — free AI may be busy, try again");
    } finally {
      setRunning(false);
    }
  };

  const agent = agents.find((a) => a.id === selected);

  return (
    <div className="space-y-6" data-testid="agency-tab">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B38B2D]">
            <Users className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Your AI Team</h2>
            <p className="text-xs font-light text-[#91837C]">
              Six specialists rebuilt from your n8n agent bundle and the open-source agency roster — running natively, on the free engine. Pick a specialist, describe the task, get the deliverable.
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((a) => (
            <button
              key={a.id}
              onClick={() => { setSelected(a.id); setResult(null); }}
              data-testid={`agency-agent-${a.id}`}
              className={`rounded-xl border p-3.5 text-left transition-all duration-200 ${
                selected === a.id ? "border-[#D4AF37] bg-[#F8F3E6] shadow-[0_6px_18px_rgba(212,175,55,0.12)]" : "border-[#EBE3DA] bg-white hover:border-[#D4AF37]/50"
              }`}
            >
              <span className="block text-sm font-semibold text-[#1A1615]">{a.name}</span>
              <span className="mt-0.5 block text-[10px] font-light leading-relaxed text-[#91837C]">{a.tagline}</span>
            </button>
          ))}
        </div>
      </div>

      {agent && (
        <div className="glass rounded-2xl p-6" data-testid="agency-run-panel">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">{agent.name} — task</p>
          <p className="mt-1 text-xs font-light text-[#91837C]">{agent.input_label}</p>
          <div className="mt-3 flex gap-2">
            <input
              className="input-lux flex-1 text-sm"
              placeholder={agent.input_placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !running && run()}
              data-testid="agency-input"
            />
            <button onClick={run} disabled={running} data-testid="agency-run" className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-[10px] disabled:opacity-60">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run
            </button>
          </div>
          {running && <p className="mt-2 text-[10px] font-light text-[#91837C]">{agent.kind === "leads" ? "Searching Dubai businesses and hunting contact emails…" : "Your specialist is working — about 30–60 seconds on the free engine…"}</p>}
        </div>
      )}

      {/* lead finder results */}
      {result?.leads && (
        <div className="glass rounded-2xl p-6" data-testid="agency-leads-result">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg font-semibold text-[#1A1615]">{result.leads.length} prospects found</h3>
            <button
              onClick={async () => {
                const { data } = await api.post("/admin/agency/leads-to-crm", { leads: result.leads });
                toast.success(`${data.added} prospects added to Leads CRM`);
              }}
              data-testid="agency-leads-to-crm"
              className="btn-gold px-5 py-2 text-[10px]"
            >
              Add All to Leads CRM
            </button>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {result.leads.map((l, i) => (
              <div key={l.id} className="rounded-xl border border-[#EFE7DF] bg-white/70 px-4 py-3" data-testid={`agency-lead-${i}`}>
                <p className="text-xs font-semibold text-[#1A1615]">{l.name}</p>
                <a href={l.website} target="_blank" rel="noreferrer" className="text-[10px] text-[#B38B2D] hover:underline">{l.website}</a>
                <p className="mt-0.5 text-[10px] font-light text-[#91837C]">{l.email ? `Email: ${l.email}` : "No public email found"}</p>
              </div>
            ))}
            {!result.leads.length && <p className="text-xs font-light text-[#91837C]">No prospects surfaced — try a broader niche.</p>}
          </div>
        </div>
      )}

      {/* text deliverable */}
      {result && !result.leads && result.output && (
        <div className="glass rounded-2xl p-6" data-testid="agency-output">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg font-semibold text-[#1A1615]">{result.agent_name} — deliverable</h3>
            <button
              onClick={() => { navigator.clipboard.writeText(result.output); toast.success("Copied"); }}
              data-testid="agency-copy"
              className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2 text-[10px]"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-white/70 p-4 font-sans text-xs leading-relaxed text-[#3A322E]">{result.output}</pre>
        </div>
      )}

      {/* history */}
      {history.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="agency-history">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">
            <History className="h-3.5 w-3.5" /> Recent runs
          </p>
          <div className="mt-3 space-y-2">
            {history.slice(0, 10).map((r) => (
              <button
                key={r.id}
                onClick={() => { setSelected(r.agent_id); setResult(r); }}
                className="flex w-full items-center gap-3 rounded-xl bg-white/60 px-4 py-2.5 text-left transition-colors hover:bg-[#F8F3E6]"
                data-testid={`agency-run-${r.id}`}
              >
                <span className="rounded-full bg-[#F5EBE6] px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[#8a6d1f]">{r.agent_name}</span>
                <span className="min-w-0 flex-1 truncate text-xs font-light text-[#5A504B]">{r.input}</span>
                <span className="text-[9px] text-[#A39288]">{new Date(r.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
